/** @format */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Skeleton,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchSkillTreeLevelOneTwo,
  fetchSkillTreeNextLevel,
} from "./techrelevance.apis/fetchSkillTree";
import {
  setSkillTreeCache,
  generateCacheKey,
} from "../redux/action/skillTreeCache";
import toast from "react-hot-toast";

// Function to render custom icons
const renderCustomIcon = (iconName, size = 24) => {
  const iconPath = `/assets/images/bookmark-icons/${iconName}.svg`;
  return (
    <img
      src={iconPath}
      alt={iconName}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
      }}
      onError={(e) => {
        console.warn(`Failed to load custom icon: ${iconName}`);
        e.target.style.display = "none";
      }}
    />
  );
};

/**
 * Convert skill tree structure to ApexTree format
 * @param {Object} node - Skill tree node
 * @param {number} startIndex - Starting index for node IDs
 * @returns {Object} Formatted data for ApexTree
 */
const mapToApexTree = (node, startIndex = 1) => {
  let currentIndex = startIndex;

  const processNode = (node, level = 0, parentIndex = 0, branchIndex = -1) => {
    const nodeIndex = currentIndex++;
    const rawNodeId = node.id || nodeIndex;
    // Ensure node ID is a valid CSS selector by prefixing with "node-"
    // This prevents errors when IDs start with numbers
    const nodeId = `node-${rawNodeId}`;
    const nodeName = node.skillName || node.name || `Node ${nodeIndex}`;
    const nodeLevel = node.level !== undefined ? node.level : level;

    // Determine branch index: level 1 nodes start new branches
    let currentBranchIndex = branchIndex;
    if (nodeLevel === 1) {
      currentBranchIndex = parentIndex;
    }

    // Use branch-specific colors
    const colors = getColorsForLevel(nodeLevel, currentBranchIndex);

    // Level 2 nodes (second level from API, levels start at 1) should always have empty children initially (user can expand manually)
    // Check if node has children property and if it's not a level 2 node
    const hasChildren = node.children !== undefined && node.children !== null;
    const shouldHaveChildren = nodeLevel !== 2 && hasChildren && node.children.length > 0;

    return {
      id: nodeId,
      data: {
        id: nodeId, // Include id in data object for template access
        name: nodeName,
        index: nodeIndex,
        level: nodeLevel,
        branchIndex: currentBranchIndex, // Store branch index for children and styling
        mainColor: colors.mainColor, // Store main color for gradient border
        skillName: nodeName,
        backgroundColor: colors.backgroundColor,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
      },
      // Level 2 nodes always get empty children array, regardless of input
      children: nodeLevel === 2
        ? []
        : (shouldHaveChildren
          ? node.children.map((child, idx) => {
            // Determine child level - if child has level property, use it, otherwise increment
            const childLevel = child.level !== undefined ? child.level : nodeLevel + 1;
            return processNode(child, childLevel, idx, currentBranchIndex);
          })
          : []),
    };
  };

  return processNode(node);
};

const branchThemes = [
  { main: "#6366F1", bg: "#EEF2FF", border: "#C7D2FE", text: "#4338CA" }, // Indigo
  { main: "#10B981", bg: "#ECFDF5", border: "#A7F3D0", text: "#047857" }, // Emerald
  { main: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" }, // Amber
  { main: "#EF4444", bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C" }, // Red
  { main: "#8B5CF6", bg: "#F5F3FF", border: "#DDD6FE", text: "#6D28D9" }, // Violet
  { main: "#06B6D4", bg: "#ECFEFF", border: "#A5F3FC", text: "#0E7490" }, // Cyan
  { main: "#EC4899", bg: "#FDF2F8", border: "#FBCFE8", text: "#BE185D" }, // Pink
  { main: "#F97316", bg: "#FFF7ED", border: "#FFEDD5", text: "#C2410C" }, // Orange
];

/**
 * Get retina-inspired gradient border for expanded nodes
 * Based on the branch's main color for a more professional look
 * @param {string} mainColor - The main color of the branch
 * @returns {string} CSS linear-gradient string for expanded node borders
 */
const getExpandedNodeBorderGradient = (mainColor) => {
  if (!mainColor) {
    // Fallback to a nice neutral gradient if no color provided
    return "linear-gradient(135deg, #6366F1, #818CF8, #6366F1)";
  }
  // Create a beautiful, subtle gradient based on the branch's main color
  // Use the color and a slightly transparent version for a glowing effect
  return `linear-gradient(135deg, ${mainColor}, ${mainColor}88, ${mainColor})`;
};

/**
 * Get branch-specific border color for expanded nodes
 * @param {string} mainColor - The main color of the branch
 * @returns {string} Hex color value for expanded node borders
 */
const getExpandedNodeBorderColorSolid = (mainColor) => {
  return mainColor || "#6366F1";
};

/**
 * Get beautiful light background colors based on skill level and branch
 * @param {number} level - Skill level
 * @param {number} branchIndex - The index of the branch (Level 1 child index)
 * @returns {Object} Object with backgroundColor, borderColor, textColor, and mainColor
 */
const getColorsForLevel = (level, branchIndex = -1) => {
  // Level 0 (Root) always has its own fixed theme
  if (level === 0) {
    return {
      backgroundColor: "#F3E8FF", // Light purple for root
      borderColor: "#C4B5FD",
      textColor: "#6C52E5",
      mainColor: "#7C3AED",
    };
  }

  // Use branch-specific themes for level 1 and above
  if (branchIndex !== -1) {
    const theme = branchThemes[branchIndex % branchThemes.length];
    return {
      backgroundColor: theme.bg,
      borderColor: theme.border,
      textColor: theme.text,
      mainColor: theme.main,
    };
  }

  // Fallback to original level-based colors if no branch index is available
  const colorMap = {
    1: {
      backgroundColor: "#E8E4F7",
      borderColor: "#8B7AE8",
      textColor: "#4A3D9E",
      mainColor: "#6366F1",
    },
    2: {
      backgroundColor: "#E0F2FE",
      borderColor: "#7DD3FC",
      textColor: "#0C4A6E",
      mainColor: "#0EA5E9",
    },
    3: {
      backgroundColor: "#FDF2F8",
      borderColor: "#F9A8D4",
      textColor: "#831843",
      mainColor: "#EC4899",
    },
    4: {
      backgroundColor: "#F0FDF4",
      borderColor: "#86EFAC",
      textColor: "#166534",
      mainColor: "#22C55E",
    },
  };

  return colorMap[level] || {
    backgroundColor: "#F9FAFB",
    borderColor: "#E5E7EB",
    textColor: "#374151",
    mainColor: "#6B7280",
  };
};

const SkillTree = ({ skillName, resultId, checkSkill }) => {
  const dispatch = useDispatch();
  const skillTreeCache = useSelector((state) => state.skillTreeCache?.cache || {});

  const containerRef = useRef(null);
  const treeInstanceRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [level1And2Data, setLevel1And2Data] = useState(null);
  const [isDataFromCache, setIsDataFromCache] = useState(false);
  const [isSwitchingModel, setIsSwitchingModel] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set()); // Track expanded nodes
  const [loadingNodes, setLoadingNodes] = useState(new Set()); // Track nodes being loaded
  const [treeDataState, setTreeDataState] = useState(null); // Store current tree data state
  const [collapsedNodes, setCollapsedNodes] = useState(new Set()); // Track manually collapsed nodes
  const [treeInitialized, setTreeInitialized] = useState(false); // Track if tree has been initialized
  
  // Detect if device is mobile and set default tree direction
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
  };
  
  const [treeDirection, setTreeDirection] = useState(isMobileDevice() ? 'left' : 'top'); // Tree direction: 'bottom', 'top', 'left', 'right'
  const aiConfiguration = useSelector((state) => state.skillTreeCache?.aiConfiguration);
  const [selectedModel, setSelectedModel] = useState('gemini'); // Selected AI model

  // Derive available models from Redux configuration
  const availableModels = useMemo(() => {
    if (!aiConfiguration) {
      return [
        { label: 'GPT', value: 'gpt' },
        { label: 'Gemini', value: 'gemini' },
        { label: 'Claude', value: 'claude' }
      ];
    }
    const configData = aiConfiguration["get-ai-configuration"] || aiConfiguration.result || aiConfiguration;
    const models = configData.aiProvider || configData.models || configData.result || [];
    return Array.isArray(models) && models.length > 0 ? models : [
      { label: 'GPT', value: 'gpt' },
      { label: 'Gemini', value: 'gemini' },
      { label: 'Claude', value: 'claude' }
    ];
  }, [aiConfiguration]);

  // Set initial selected model when availableModels changes
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      setSelectedModel(availableModels[0].value || availableModels[0].id || 'gemini');
    }
  }, [availableModels, selectedModel]);

  const expandNodeRef = useRef(null); // Ref to store expandNode function for inline handlers

  const expandedNodesRef = useRef(new Set()); // Ref to track expanded nodes for event handlers
  const loadingNodesRef = useRef(new Set()); // Ref to track loading nodes for event handlers
  const dragStateRef = useRef({ isDragging: false, startX: 0, startY: 0 }); // Track drag state to prevent accidental clicks
  const lastExpandedNodeRef = useRef(null); // Track the last node that was expanded via API
  const treeDataStateRef = useRef(null); // Ref to track tree data state for event handlers

  // Load Level 1+2 data on mount or when model changes
  useEffect(() => {
    if (skillName && resultId && selectedModel) {
      loadLevel1And2();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillName, resultId, selectedModel]);

  const loadLevel1And2 = useCallback(async () => {
    if (!skillName || !resultId) return;

    // Show loading state when switching models
    setIsSwitchingModel(true);

    // Reset tree state first to ensure clean state
    setTreeDataState(null);
    setExpandedNodes(new Set());
    setLoadingNodes(new Set());
    setCollapsedNodes(new Set());
    setLevel1And2Data(null);
    setError(null);
    setIsDataFromCache(false);
    lastExpandedNodeRef.current = null;

    // Generate cache key
    const cacheKey = generateCacheKey(skillName, resultId, selectedModel);

    // Check if data exists in cache
    const cachedData = skillTreeCache[cacheKey];
    if (cachedData && cachedData.level1And2Data) {
      // Use cached data - show loader briefly for smooth transition
      setTimeout(() => {
        setLevel1And2Data(cachedData.level1And2Data);
        setIsDataFromCache(true);
        setLoading(false);
        setIsSwitchingModel(false);
      }, 300); // Brief delay to show skeleton for smooth UX
      return;
    }

    // Reset cache flag if fetching from API
    setIsDataFromCache(false);

    // If not in cache, fetch from API
    setLoading(true);
    setError(null);

    try {
      const response = await fetchSkillTreeLevelOneTwo(skillName, resultId, selectedModel);
      const responseBody = response.body?.result || response.body;
      setLevel1And2Data(responseBody);

      // Store in cache after successful fetch
      dispatch(setSkillTreeCache(cacheKey, responseBody));
    } catch (err) {
      const errorMessage = err.message || "Failed to load skill tree data";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setIsSwitchingModel(false);
    }
  }, [skillName, resultId, selectedModel, skillTreeCache, dispatch]);


  // Function to expand a node and load its children
  const expandNode = useCallback(async (nodeId, nodeData) => {
    const nodeLevel = nodeData?.data?.level || nodeData?.level || 0;
    

    // Check the level of the node - prevent expansion for level 5 nodes
    if (nodeLevel >= 5) {
      // console.log('❌ Cannot expand level 5 nodes - maximum depth reached');
      return;
    }

    // Check if node is already loading
    if (loadingNodes.has(nodeId)) {
      // console.log('⏳ Node already loading:', nodeId);
      return;
    }

    // IMPORTANT: Check if node already has children in treeDataState (cached data)
    // This prevents API calls when user collapses and expands again
    if (nodeData.children && nodeData.children.length > 0) {
      // console.log('✅ Node already has cached children, skipping API call:', nodeId, 'children count:', nodeData.children.length);
      // Mark as expanded so ApexTree can handle the expand/collapse
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.add(nodeId);
        return newSet;
      });
      return;
    }

    // Check if node is already expanded (but this shouldn't prevent loading if no children)
    if (expandedNodes.has(nodeId) && (!nodeData.children || nodeData.children.length === 0)) {
      // console.log('Node marked as expanded but has no children, will load:', nodeId);
      // Remove from expanded set so we can load
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }

    setLoadingNodes((prev) => new Set(prev).add(nodeId));

    try {
      // Get node data - use nodeData.data if available, otherwise use nodeData directly
      const rawNodeId = nodeData?.data?.id || nodeData?.id || nodeId;
      // Remove "node-" prefix for API call (API expects raw IDs)
      const apiNodeId = rawNodeId.startsWith('node-') ? rawNodeId.substring(5) : rawNodeId;
      
      const nodeInfo = {
        id: apiNodeId,
        skillName: nodeData?.data?.skillName || nodeData?.data?.name || nodeData?.skillName || nodeData?.name,
        level: nodeData?.data?.level || nodeData?.level || 2,
      };

      // console.log('Calling API with node info:', nodeInfo);

      // Pass selectedModel to ensure all levels (Level 2, Level 3, Level 4, etc.) use the correct AI model
      const response = await fetchSkillTreeNextLevel(
        nodeInfo,
        resultId,
        skillName,
        selectedModel
      );

      // console.log('API response received:', response);

      const responseBody = response.body?.result || response.body;
      // API returns node with children, or separate children array
      const updatedNode = responseBody.node || responseBody;
      const newChildren = updatedNode.children || responseBody.children || [];
      const nextLevel = responseBody.level || (nodeData.data?.level || 2) + 1;

      // Update tree data state with new children
      setTreeDataState((prevTree) => {
        if (!prevTree) {
          console.warn('⚠️ prevTree is null, cannot update children for node:', nodeId);
          return prevTree;
        }

        const updateNodeChildren = (node) => {
          if (node.id === nodeId) {
            // Get branch index from parent node
            const branchIndex = node.data?.branchIndex ?? -1;

            // Convert API children to ApexTree format
            // All newly loaded children should start collapsed (empty array)
            // They will load their own children when user expands them
            const apexChildren = newChildren.map((child, idx) => {
              const childLevel = nextLevel; // Use the level from API response
              const colors = getColorsForLevel(childLevel, branchIndex);
              // Ensure child ID is valid CSS selector
              const childId = child.id ? `node-${child.id}` : `child-${nodeId}-${idx}`;
              return {
                id: childId,
                data: {
                  id: childId, // Include id in data object for consistency
                  name: child.skillName || child.name,
                  index: idx + 1,
                  level: childLevel,
                  branchIndex: branchIndex, // Pass branch index down
                  mainColor: colors.mainColor, // Pass main color for styling
                  skillName: child.skillName || child.name,
                  backgroundColor: colors.backgroundColor,
                  borderColor: colors.borderColor,
                  textColor: colors.textColor,
                },
                // Always start with empty children - load on demand
                children: [],
              };
            });            
            return { ...node, children: apexChildren };
          }
          if (node.children && node.children.length > 0) {
            return {
              ...node,
              children: node.children.map(updateNodeChildren),
            };
          }
          return node;
        };

        return updateNodeChildren(prevTree);
      });

      // Mark node as expanded after successfully loading children
      // This helps track which nodes have been loaded, but ApexTree will handle visual expand/collapse
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.add(nodeId);
        // console.log(`✅ Marked node ${nodeId} as expanded. Total expanded nodes: ${newSet.size}`);
        return newSet;
      });

      // Get the level of the expanded node from nodeData
      const expandedNodeLevel = nodeData?.data?.level || nodeData?.level || 0;

      // Helper function to find all nodes, their parent, siblings, and ancestors
      const findTreeInfo = (tree, targetId) => {
        const nodesByLevel = {};
        let targetParent = null;
        const ancestors = [];
        
        const traverse = (node, parentNode = null, parentChain = []) => {
          const nodeLevel = node.data?.level || 0;
          
          // Track node by level
          if (!nodesByLevel[nodeLevel]) {
            nodesByLevel[nodeLevel] = [];
          }
          nodesByLevel[nodeLevel].push(node);
          
          // Find parent and ancestors of target node
          if (String(node.id) === String(targetId)) {
            targetParent = parentNode;
            ancestors.push(...parentChain);
          }
          
          // Traverse children
          if (node.children && node.children.length > 0) {
            node.children.forEach((child) => traverse(child, node, [...parentChain, node]));
          }
        };
        
        if (tree) traverse(tree);
        return { nodesByLevel, targetParent, ancestors };
      };

      // Use the current tree state BEFORE the update (via ref to avoid re-renders)
      const currentTreeBeforeUpdate = treeDataStateRef.current;
      const { nodesByLevel, targetParent, ancestors } = findTreeInfo(currentTreeBeforeUpdate, nodeId);
      

      // Apply exclusive expansion for ALL levels (1-5)
      setCollapsedNodes((prev) => {
        const newSet = new Set(prev);
        
        // For levels 1-5, collapse all siblings at the same level
        if (expandedNodeLevel >= 1) {
          // Get siblings (nodes at the same level with the same parent)
          const siblingsAtLevel = (nodesByLevel[expandedNodeLevel] || []).filter((node) => {
            const nodeIdStr = String(node.id);
            // Don't count the current node as a sibling
            if (nodeIdStr === nodeId) return false;
            
            // If we have a parent, check if this node shares the same parent
            if (targetParent) {
              // Find this node's parent by checking if it's in targetParent's children
              const isDirectSibling = targetParent.children?.some(child => String(child.id) === nodeIdStr);
              return isDirectSibling;
            }
            
            // If no parent (shouldn't happen for level 1+), consider all nodes at same level
            return true;
          });                  
        } else {
          // Level 0 (root) - just remove from collapsed nodes
          newSet.delete(nodeId);
        }                
        return newSet;
      });

      // Store the node ID that was just expanded so we can ensure only it stays expanded
      lastExpandedNodeRef.current = nodeId;

      // console.log('✅ Successfully updated tree with new children for node:', nodeId, 'children count:', newChildren.length);
    } catch (err) {
      const errorMessage = err.message || "Failed to load next level data";
      toast.error(errorMessage);
    } finally {
      setLoadingNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }
  }, [expandedNodes, loadingNodes, resultId, skillName, selectedModel]);

  // Store expandNode in ref for access from event handlers
  useEffect(() => {
    expandNodeRef.current = expandNode;
  }, [expandNode]);

  // Keep refs in sync with state
  useEffect(() => {
    expandedNodesRef.current = expandedNodes;
  }, [expandedNodes]);

  useEffect(() => {
    loadingNodesRef.current = loadingNodes;
  }, [loadingNodes]);

  useEffect(() => {
    treeDataStateRef.current = treeDataState;
  }, [treeDataState]);


  // Reset treeDataState when skill or model changes
  useEffect(() => {
    // Only reset if we're actually changing (to avoid unnecessary resets)
    setTreeDataState(null);
    setExpandedNodes(new Set());
    setLoadingNodes(new Set());
    setCollapsedNodes(new Set());
    setIsDataFromCache(false); // Reset cache flag
    setTreeInitialized(false); // Reset initialization flag
    lastExpandedNodeRef.current = null;
    // Don't reset level1And2Data here - let loadLevel1And2 handle it
  }, [skillName, resultId, selectedModel]);

  // Initialize treeDataState when we have data (only when source data changes)
  useEffect(() => {
    if (!level1And2Data) {
      return;
    }

    // If tree is already initialized, don't reset it
    // This prevents losing dynamically loaded deeper levels
    if (treeInitialized) {      
      return;
    }

    // Use Level 1+2 data
    const dataSource = level1And2Data;

    // Get tree structure from API response
    let treeStructure =
      dataSource?.treeStructure ||
      dataSource?.result?.treeStructure;

    // If treeStructure is an array, extract the root object (first element)
    if (Array.isArray(treeStructure) && treeStructure.length > 0) {
      treeStructure = treeStructure[0];
    }

    // If treeStructure is not available, build it from latestTechnologies
    if (!treeStructure) {
      const latestTech =
        dataSource?.latestTechnologies ||
        dataSource?.result?.latestTechnologies ||
        [];

      if (latestTech.length === 0) {
        return;
      }

      // Build tree structure: Level 0 (root), Level 1, Level 2 from API
      // API levels start at 1, so level 1 = first level, level 2 = second level
      // Level 2 nodes will have empty children initially and can be expanded to load Level 3
      treeStructure = {
        name: checkSkill || skillName,
        id: "root",
        skillName: checkSkill || skillName,
        level: 0,
        children: latestTech.map((item) => ({
          name: item.skillName,
          skillName: item.skillName,
          id: item.id,
          level: item.level !== undefined ? item.level : 1, // Use API level if available, default to 1
          children: (item.children || []).map((child) => ({
            name: child.skillName,
            skillName: child.skillName,
            id: child.id,
            level: child.level !== undefined ? child.level : 2, // Use API level if available, default to 2
            children: [], // Level 2 nodes start empty - will load Level 3 on expand
          })),
        })),
      };
    }

    if (!treeStructure) {
      return;
    }

    // Remove level 2 children from tree structure before mapping
    // This creates a deep copy to avoid mutating the original structure
    // Level 2 nodes (second level from API) should have empty children initially
    const processTreeStructure = (node) => {
      const nodeLevel = node.level !== undefined ? node.level : 0;
      if (nodeLevel === 2) {
        // For level 2 nodes, set children to empty array
        // mapToApexTree will also ensure they're empty, but this provides an extra safeguard
        return {
          ...node,
          children: []
        };
      }
      // For other levels, recursively process children
      return {
        ...node,
        children: (node.children || []).map(processTreeStructure)
      };
    };

    treeStructure = processTreeStructure(treeStructure);

    const mappedTree = mapToApexTree(treeStructure);
    // Always rebuild tree when level1And2Data changes (for both API and cache)
    // This ensures the tree is properly initialized when switching models or loading from cache
    const currentSkillName = checkSkill || skillName;

    // Mark Level 1 and Level 2 nodes as collapsed initially
    // Only the first Level 1 node will remain expanded
    // API levels start at 1, so level 1 and level 2 should be collapsed
    const level1NodeIds = new Set();
    const level2NodeIds = new Set();
    let firstLevel1Id = null;
    
    const markNodesAsCollapsed = (node) => {
      const nodeLevel = node.data?.level || 0;
      if (nodeLevel === 1) {
        const nodeId = String(node.id);
        level1NodeIds.add(nodeId);
        // Keep track of the first Level 1 node
        if (firstLevel1Id === null) {
          firstLevel1Id = nodeId;
        }
      } else if (nodeLevel === 2) {
        const nodeId = String(node.id);
        level2NodeIds.add(nodeId);
      }
      // Recursively process children
      if (node.children && node.children.length > 0) {
        node.children.forEach(markNodesAsCollapsed);
      }
    };

    // Collect all level 1 and level 2 node IDs
    markNodesAsCollapsed(mappedTree);

    // Set Level 1 nodes (except first) and all Level 2 nodes as collapsed
    setCollapsedNodes((prevCollapsed) => {
      const newSet = new Set(prevCollapsed);
      
      // Collapse all Level 1 nodes except the first one
      level1NodeIds.forEach((nodeId) => {
        if (nodeId !== firstLevel1Id) {
          newSet.add(nodeId);
        }
      });
      
      // Collapse all Level 2 nodes
      level2NodeIds.forEach((nodeId) => newSet.add(nodeId));
      
      return newSet;
    });

    // Set the new tree - this ensures cache-loaded data displays properly
    setTreeDataState(mappedTree);
    
    // Mark tree as initialized to prevent future resets
    setTreeInitialized(true);    
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level1And2Data, checkSkill, skillName]);

  // Prepare tree data for ApexTree - use treeDataState
  // Control initial expansion through data structure:
  // - Level 0 and 1: have children (expanded by ApexTree)
  // - Level 2: no children initially (collapsed by ApexTree)
  const treeData = useMemo(() => {
    if (!treeDataState) return null;    

    // Process tree based on level and user interaction state
    const prepareTreeData = (node, depth = 0) => {
      const nodeId = String(node.id);
      const nodeLevel = node.data?.level || 0;
      const isCollapsed = collapsedNodes.has(nodeId);
      const isExpanded = expandedNodes.has(nodeId);
      const hasChildren = node.children && node.children.length > 0;
    
      // Levels 2-5: collapsed by default (no children)
      // Only show children if user explicitly expanded them
      if (nodeLevel >= 2) {
        // Check if user has expanded this specific node
        if (isExpanded && !isCollapsed) {
          // User clicked to expand - show children
          const processedChildren = (node.children || []).map(child => prepareTreeData(child, depth + 1));          
          return {
            ...node,
            children: processedChildren
          };
        }
        // Default: level 2-5 nodes are collapsed (empty children array)
        // ApexTree will render them as collapsed since they have no children        
        return {
          ...node,
          children: []
        };
      }

      // Level 0 and Level 1: expanded by default (show children)
      // But respect manual collapse state
      if (isCollapsed) {        
        return {
          ...node,
          children: []
        };
      }

      // For expanded nodes (Level 0, 1, or manually expanded), show children
      const processedChildren = (node.children || []).map(child => prepareTreeData(child, depth + 1));      
      return {
        ...node,
        children: processedChildren
      };
    };

    const result = prepareTreeData(treeDataState);    
    return result;
  }, [treeDataState, collapsedNodes, expandedNodes]);

  // Helper function to find node in tree - use treeDataState for latest data
  // Defined here to be accessible by both treeOptions and event handlers
  const findNodeInTree = useCallback((node, targetId) => {
    if (!node) return null;

    // Try exact match first
    if (String(node.id) === String(targetId)) {
      return node;
    }

    // Try matching with data.id if it exists
    if (node.data && String(node.data.id) === String(targetId)) {
      return node;
    }

    // Recursively search children
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const found = findNodeInTree(child, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Tree options for ApexTree
  const treeOptions = useMemo(
    () => ({
      contentKey: "data",
      width: "100%",
      height: "auto",
      nodeWidth: 220,
      nodeHeight: 70,
      childrenSpacing: 50,
      siblingSpacing: 20,
      direction: treeDirection, // Dynamic direction based on user selection
      enableExpandCollapse: false, // Disable default collapsible/expandable nodes to handle it manually
      nodeTemplate: (content, nodeData) => {
        // ApexTree with contentKey="data" passes:
        // - content: the data object (what we put in node.data)
        // - nodeData: the full node object (with id, children, etc.) - may or may not be available
        // Get node ID from content.id (which we now include in data) or nodeData.id
        const nodeId = content?.id || nodeData?.id || 'unknown';

        // Use treeDataState (the absolute source of truth) to check for children
        // This avoids issues where treeData or nodeData might have hidden children due to collapse state
        let hasChildrenInState = false;

        // Check if ApexTree already has children (visible children)
        const apexHasChildren = (nodeData && nodeData.children && nodeData.children.length > 0);

        if (treeDataState) {
          const nodeInState = findNodeInTree(treeDataState, nodeId);
          const stateHasChildren = (nodeInState && nodeInState.children && nodeInState.children.length > 0);

          // It has children if either state has them OR ApexTree has them (visible)
          hasChildrenInState = stateHasChildren || apexHasChildren || false;

          // Debug logging for Level 3 nodes
          if (content?.level === 3) {
            // Only log if there's a discrepancy or it's relevant
            if (apexHasChildren && !stateHasChildren) {
              console.warn('⚠️ Level 3 node has visible children but not found in state:', {
                nodeName: content?.name,
                nodeId,
                apexHasChildren,
                stateHasChildren
              });
            }
          }
        } else {
          // Fallback if state not ready
          hasChildrenInState = apexHasChildren || false;
        }

        const isLoading = loadingNodes.has(nodeId);
        const isCollapsed = collapsedNodes.has(nodeId);
        const nodeLevel = content?.level || 0;

        // Determine if we should show a toggle button
        // Show for:
        // 1. Levels 1, 2, 3 (can load children or have children)
        // 2. Any node that already has children (can collapse/expand)
        // 3. Any node that is explicitly collapsed
        // We exclude Level 0 (Root) and Level 4 (Leaves) from having expand buttons by default unless they have children
        const canExpand = (nodeLevel >= 1 && nodeLevel <= 3) || hasChildrenInState || isCollapsed;

        // Determine button state
        // It is expanded if it has children in the state AND it is not explicitly collapsed
        const isNodeExpanded = hasChildrenInState && !isCollapsed;

        // More debug logging
        if (content?.level === 3 && hasChildrenInState) {
          // console.log('Level 3 icon state:', {
          //   nodeName: content?.name,
          //   isNodeExpanded,
          //   buttonSymbol: isNodeExpanded ? "-" : "+"
          // });
        }

        // Button symbol: "-" if expanded, "+" if collapsed
        const buttonSymbol = isNodeExpanded ? "-" : "+";

        // Get border color: use branch-specific gradient for expanded nodes
        const expandedBorderGradient = getExpandedNodeBorderGradient(content.mainColor);
        const expandedBorderColor = getExpandedNodeBorderColorSolid(content.mainColor);
        const nodeBorderColor = isNodeExpanded ? expandedBorderColor : content.borderColor;

        // Show toggle button if applicable
        // Use larger size on mobile for better touch targets (min 44x44px recommended)
        const expandIcon = canExpand && !isLoading
          ? `<span class="expand-icon" data-action="${isNodeExpanded ? 'collapse' : 'expand'}" style="position: absolute; top: 50%; right: 8px; transform: translateY(-50%); font-size: 22px; font-weight: bold; color: ${content.textColor}; opacity: 0.9; line-height: 1; cursor: pointer; user-select: none; z-index: 100; width: 28px; height: 28px; min-width: 28px; min-height: 28px; display: flex; align-items: center; justify-content: center; background: rgba(255, 255, 255, 0.95); border-radius: 50%; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15); border: 2px solid ${nodeBorderColor}; transition: all 0.2s ease; touch-action: none; -webkit-tap-highlight-color: transparent;">${buttonSymbol}</span>`
          : '';

        // Apply shimmer effect to the entire node when loading
        // Use inline style with CSS variables for dynamic color-based shimmer
        const shimmerClass = isLoading ? 'shimmer-loading' : '';
        const shimmerStyle = isLoading
          ? `--shimmer-base-color: ${content.backgroundColor}; --shimmer-highlight: rgba(255, 255, 255, 0.6); --shimmer-border: ${nodeBorderColor};`
          : '';

        // Create border style: use vibrant gradient for expanded nodes, default for others
        // Using background-clip trick for gradient border with rounded corners
        const borderStyle = isNodeExpanded
          ? `border: 2px solid transparent; background-image: linear-gradient(${content.backgroundColor}, ${content.backgroundColor}), ${expandedBorderGradient}; background-origin: border-box; background-clip: padding-box, border-box; box-shadow: 0 0 8px ${expandedBorderColor}50, 0 4px 12px ${expandedBorderColor}40;`
          : `border: 2px solid ${nodeBorderColor}; background-color: ${content.backgroundColor}; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06);`;

        // Reduced padding top/bottom from 12px to 6px as requested
        const nodeStyle = `position: relative; display: flex; align-items: center; justify-content: center; height: 100%; ${borderStyle} border-radius: 16px; padding: 6px ${canExpand || isLoading ? '40px' : '16px'} 6px 16px; box-sizing: border-box; font-family: 'Poppins', sans-serif; transition: all 0.3s ease; cursor: default; ${isLoading ? `overflow: hidden; ${shimmerStyle}` : ''}`;

        return `
          <div class="skill-node ${shimmerClass}" data-node-id="${nodeId}" data-node-level="${nodeLevel}" data-bg-color="${content.backgroundColor}" style="${nodeStyle}">
            <div class="node-title" style="font-size: 14px; font-weight: 700; color: ${content.textColor}; line-height: 1.3; text-align: center; word-wrap: break-word; overflow-wrap: break-word; width: 100%; position: relative; z-index: 1;">
              ${content.name}
            </div>
            ${expandIcon}
          </div>
        `;
      },
      nodeStyle: `
        border-radius: 16px;
        overflow: visible;
        transition: all 0.3s ease;
      `,
      enableZoom: false, // Disable zoom to prevent zoom out on button click
      enablePan: false, // Disable pan to prevent movement on button click
      canvasStyle: `
        background: linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%);
        border-radius: 24px;
        border: 1px solid #E5E7EB;
        height: 600px;
        padding: 20px;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
        overflow: auto;
      `,
      enableToolbar: false, // Disable toolbar for cleaner look
      // Note: Initial expansion is controlled by treeData structure
      // Level 0 and 1 nodes have children (expanded), Level 2 nodes have empty children (collapsed)
    }),
    [expandedNodes, loadingNodes, collapsedNodes, treeDataState, treeDirection, findNodeInTree]
  );

  // Cleanup function to destroy tree instance
  const cleanupTree = useCallback(() => {
    if (treeInstanceRef.current) {
      try {
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }
        treeInstanceRef.current = null;
      } catch (error) {
        console.warn("Error cleaning up tree:", error);
      }
    }
  }, []);

  // Handle node clicks for expansion using MutationObserver and direct event handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !treeData) return;

    // Unified handler for both mouse and touch events
    const getEventCoordinates = (event) => {
      if (event.touches && event.touches.length > 0) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      if (event.changedTouches && event.changedTouches.length > 0) {
        return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    };

    // Track mouse/touch down to detect drags and prevent accidental clicks
    const handlePointerDown = (event) => {
      // Only track if clicking on expand icon or skill node
      const expandIcon = event.target.closest('.expand-icon');
      const skillNode = event.target.closest('.skill-node');

      if (expandIcon || skillNode) {
        const coords = getEventCoordinates(event);
        dragStateRef.current = {
          isDragging: false,
          startX: coords.x,
          startY: coords.y,
          target: expandIcon || skillNode,
          timestamp: Date.now()
        };
      } else {
        // Reset if clicking outside
        dragStateRef.current = { isDragging: false, startX: 0, startY: 0, target: null, timestamp: 0 };
      }
    };

    const handlePointerMove = (event) => {
      const dragState = dragStateRef.current;
      // Only check for drag if we have a valid start position
      if (dragState.startX !== 0 || dragState.startY !== 0) {
        const coords = getEventCoordinates(event);
        const deltaX = Math.abs(coords.x - dragState.startX);
        const deltaY = Math.abs(coords.y - dragState.startY);
        const dragThreshold = 5; // pixels - if moved more than this, consider it a drag

        if (deltaX > dragThreshold || deltaY > dragThreshold) {
          dragStateRef.current.isDragging = true;
        }
      }
    };

    const handlePointerUp = (event) => {
      // Check if this was a quick click (not a drag)
      const dragState = dragStateRef.current;
      const clickDuration = Date.now() - (dragState.timestamp || 0);
      const maxClickDuration = 300; // milliseconds - if longer, might be a drag

      // If it was a quick click and no significant movement, it's a genuine click
      if (clickDuration < maxClickDuration && !dragState.isDragging) {
        // Allow the click to proceed - don't reset yet
        // The click handler will check isDragging and proceed if false
      } else {
        // It was a drag or long press, mark as dragging
        dragStateRef.current.isDragging = true;
      }

      // Reset drag state after a short delay to allow click handler to check it
      setTimeout(() => {
        dragStateRef.current = { isDragging: false, startX: 0, startY: 0, target: null, timestamp: 0 };
      }, 50);
    };

    // Add global mouse and touch event listeners for drag detection
    container.addEventListener('mousedown', handlePointerDown, true);
    container.addEventListener('mousemove', handlePointerMove, true);
    container.addEventListener('mouseup', handlePointerUp, true);
    container.addEventListener('touchstart', handlePointerDown, { passive: false, capture: true });
    container.addEventListener('touchmove', handlePointerMove, { passive: false, capture: true });
    container.addEventListener('touchend', handlePointerUp, { passive: false, capture: true });

    // Helper function to find parent node of a given node
    const findParentNode = (tree, targetId, parent = null) => {
      if (!tree) return null;
      
      if (String(tree.id) === String(targetId)) {
        return parent;
      }
      
      if (tree.children && tree.children.length > 0) {
        for (const child of tree.children) {
          const found = findParentNode(child, targetId, tree);
          if (found) return found;
        }
      }
      
      return null;
    };

    // Helper function to get all Level 1 nodes
    const getAllLevel1Nodes = (tree) => {
      if (!tree) return [];
      
      const level1Nodes = [];
      if (tree.children && tree.children.length > 0) {
        tree.children.forEach((child) => {
          if (child.data?.level === 1) {
            level1Nodes.push(child);
          }
        });
      }
      
      return level1Nodes;
    };

    // Helper function to get all Level 2 nodes from the tree
    const getAllLevel2Nodes = (tree) => {
      if (!tree) return [];
      
      const level2Nodes = [];
      const collectLevel2Nodes = (node) => {
        if (node.data?.level === 2) {
          level2Nodes.push(node);
        }
        if (node.children && node.children.length > 0) {
          node.children.forEach(collectLevel2Nodes);
        }
      };
      
      collectLevel2Nodes(tree);
      return level2Nodes;
    };

    // Unified handler for expand/collapse actions
    const handleExpandAction = (event, icon) => {
      // Check if this was a drag operation
      if (dragStateRef.current.isDragging) {
        // console.log('🚫 Ignoring click - detected as drag operation');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      // Prevent all default behaviors and event propagation to avoid zoom/pan
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const nodeElement = icon.closest('.skill-node');
      if (nodeElement) {
        const nodeId = nodeElement.getAttribute('data-node-id');
        const action = icon.getAttribute('data-action');

        // console.log('Expand icon clicked:', { nodeId, action });

        if (nodeId) {
          // Use treeDataStateRef for latest data (avoid re-render triggers)
          const currentTreeData = treeDataStateRef.current;
          const nodeToExpand = findNodeInTree(currentTreeData, nodeId);

          if (!nodeToExpand) {
            console.warn('⚠️ Node not found in tree:', nodeId);
            return;
          }

          const nodeLevel = nodeToExpand.data?.level || 0;

          if (action === 'collapse') {
            // User wants to collapse
            // console.log('Collapsing node:', nodeId);
            setCollapsedNodes((prev) => new Set(prev).add(nodeId));
            // We don't need to remove from expandedNodes, as collapsedNodes takes precedence in treeData memo
          } else {
            // User wants to expand
            // console.log('Expanding node:', nodeId);

            // ENHANCEMENT: Exclusive expansion for ALL levels (1-5)
            // Find parent and siblings of the node being expanded
            const findNodeParentAndSiblings = (tree, targetId) => {
              let parent = null;
              let siblings = [];
              const ancestors = [];
              
              const traverse = (node, parentNode = null, parentChain = []) => {
                // Check if this node is the target
                if (String(node.id) === String(targetId)) {
                  parent = parentNode;
                  ancestors.push(...parentChain);
                  
                  // Get siblings from parent's children
                  if (parentNode && parentNode.children) {
                    siblings = parentNode.children.filter(child => String(child.id) !== String(targetId));
                  }
                  return true;
                }
                
                // Traverse children
                if (node.children && node.children.length > 0) {
                  for (const child of node.children) {
                    if (traverse(child, node, [...parentChain, node])) {
                      return true;
                    }
                  }
                }
                return false;
              };
              
              if (tree) traverse(tree);
              return { parent, siblings, ancestors };
            };
            
            const { siblings, ancestors } = findNodeParentAndSiblings(currentTreeData, nodeId);
            
            setCollapsedNodes((prev) => {
              const newSet = new Set(prev);
              
              if (nodeLevel >= 1) {
                // Collapse all siblings at the same level
                siblings.forEach((sibling) => {
                  newSet.add(String(sibling.id));
                });
                
                // Keep ancestors expanded
                ancestors.forEach((ancestor) => {
                  newSet.delete(String(ancestor.id));
                });
                
                // Keep the current node expanded
                newSet.delete(nodeId);
              } else {
                // Level 0 (root) - just remove from collapsed nodes
                newSet.delete(nodeId);
              }
              
              return newSet;
            });

            // Check if we need to load data
            const hasChildren = nodeToExpand.children && nodeToExpand.children.length > 0;
            if (!hasChildren && expandNodeRef.current) {
              // console.log('Loading children for:', nodeId);
              expandNodeRef.current(nodeId, nodeToExpand);
            } else {
              // console.log('Children already loaded, just expanding');
              // Just updating collapsedNodes (removing it) will trigger re-render with children
            }
          }
        }
      }
    };

    // Function to attach click handler to expand icons
    const attachExpandHandlers = () => {
      const expandIcons = container.querySelectorAll('.expand-icon');
      expandIcons.forEach((icon) => {
        // Check if handler already attached (using data attribute)
        if (icon.dataset.handlerAttached === 'true') {
          return;
        }

        icon.dataset.handlerAttached = 'true';

        // Prevent zoom/pan on mousedown and touchstart
        icon.addEventListener('mousedown', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }, true);

        icon.addEventListener('touchstart', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }, { passive: false, capture: true });

        // Handle touch events (mobile) - use touchend for better reliability
        icon.addEventListener('touchend', (event) => {
          // Mark that touch was handled to prevent double-triggering with click
          icon.dataset.touchHandled = 'true';
          handleExpandAction(event, icon);
          // Reset touchHandled after a delay to allow click events on desktop
          setTimeout(() => {
            icon.dataset.touchHandled = 'false';
          }, 300);
        }, { passive: false, capture: true });

        // Handle click events (desktop and fallback for mobile)
        icon.addEventListener('click', (event) => {
          // Prevent click event if touch was already handled (to avoid double-triggering on mobile)
          if (icon.dataset.touchHandled === 'true') {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return;
          }
          handleExpandAction(event, icon);
        }, true); // Use capture phase
      });
    };

    // Container click handler - only for drag detection, NOT for triggering API calls
    // API calls should ONLY happen when clicking the + icon, not the node itself
    const handleContainerClick = (event) => {
      // Check if this was a drag operation - if so, ignore the click
      if (dragStateRef.current.isDragging) {
        // console.log('🚫 Ignoring container click - detected as drag operation');
        return;
      }

      // IMPORTANT: Only handle clicks on expand icons, not on skill nodes
      // If clicking on expand icon, it's already handled by attachExpandHandlers
      // If clicking on skill node itself, let ApexTree handle it (no API call)
      const expandIcon = event.target.closest('.expand-icon');
      if (expandIcon) {
        // Expand icon click is handled separately in attachExpandHandlers
        return;
      }

      // For any other clicks on skill nodes, let ApexTree handle expand/collapse
      // No need to track manual collapses here - the collapsedNodes approach handles it
    };

    // Set up MutationObserver to watch for new nodes
    const observer = new MutationObserver(() => {
      attachExpandHandlers();
    });

    // Start observing
    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    // Initial attachment after delay
    const timeoutId = setTimeout(() => {
      attachExpandHandlers();
      if (container) {
        container.addEventListener('click', handleContainerClick, true);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      if (container) {
        container.removeEventListener('click', handleContainerClick, true);
        container.removeEventListener('mousedown', handlePointerDown, true);
        container.removeEventListener('mousemove', handlePointerMove, true);
        container.removeEventListener('mouseup', handlePointerUp, true);
        container.removeEventListener('touchstart', handlePointerDown, true);
        container.removeEventListener('touchmove', handlePointerMove, true);
        container.removeEventListener('touchend', handlePointerUp, true);
      }
    };
  }, [treeData, treeDataState, expandedNodes, loadingNodes]);

  // Render tree using ApexTree
  useEffect(() => {
    cleanupTree();

    if (treeData && typeof window !== 'undefined' && window.ApexTree && containerRef.current) {
      try {
        treeInstanceRef.current = new window.ApexTree(
          containerRef.current,
          treeOptions
        );
        treeInstanceRef.current.render(treeData);

      } catch (error) {
        console.error("Error rendering skill tree:", error);
      }
    }

    return cleanupTree;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, treeOptions, cleanupTree]);

  if (!skillName || !resultId) {
    return null;
  }

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) skewX(-20deg);
            }
            100% {
              transform: translateX(200%) skewX(-20deg);
            }
          }
          
          .shimmer-loading {
            position: relative;
            overflow: hidden;
          }
          
          .shimmer-loading::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent 0%,
              rgba(255, 255, 255, 0.0) 20%,
              rgba(255, 255, 255, 0.8) 50%,
              rgba(255, 255, 255, 0.0) 80%,
              transparent 100%
            );
            animation: shimmer 1.6s cubic-bezier(0.4, 0, 0.2, 1) infinite;
            z-index: 1;
            pointer-events: none;
            border-radius: 16px;
          }
          
          .expand-icon:hover {
            background: rgba(79, 70, 229, 0.1) !important;
            border-color: #6366F1 !important;
            transform: translateY(-50%) scale(1.1) !important;
            box-shadow: 0 4px 8px rgba(79, 70, 229, 0.3) !important;
          }
          .expand-icon:active {
            transform: translateY(-50%) scale(0.95) !important;
          }
          
          /* Prevent zoom/pan on expand icon clicks */
          .expand-icon {
            pointer-events: auto !important;
            touch-action: none !important;
            -webkit-tap-highlight-color: transparent !important;
            -webkit-touch-callout: none !important;
            user-select: none !important;
          }
          
          .skill-node {
            touch-action: pan-x pan-y !important;
          }

          /* Mobile-specific styles for better touch interaction */
          @media (max-width: 768px) {
            .expand-icon {
              width: 48px !important;
              height: 48px !important;
              min-width: 48px !important;
              min-height: 48px !important;
              font-size: 24px !important;
              right: 4px !important;
            }
            
            .skill-node {
              padding-right: 56px !important;
            }
          }
        `}
      </style>
      <Box
        id="skill-tree"
        className="skill-tree"
        sx={{
          p: { xs: 2, sm: 4, md: 3, lg: 5, xl: 5 },
          border: "1px solid #000088",
          borderRadius: "39px",
          background: "#F3FBFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 2,
          }}
        >
          {renderCustomIcon("sub-skills", 40)}
          <Typography
            variant="h6"
            sx={{
              fontFamily: "poppins",
              fontWeight: 700,
              textAlign: "center",
              color: "#000088",
            }}
          >
            Skill Tree for{" "}
            <span style={{ fontWeight: 700 }}>{checkSkill || skillName}</span>
          </Typography>
        </Box>

        {/* Direction Selector */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
              mr: 1,
            }}
          >
            Tree Direction:
          </Typography>



          {/* Top Direction */}
          <Box
            onClick={() => setTreeDirection('top')}
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              border: treeDirection === 'top' ? "2px solid #4F46E5" : "2px solid #E5E7EB",
              background: treeDirection === 'top' ? "#EEF2FF" : "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#4F46E5",
                background: "#F5F3FF",
                transform: "scale(1.05)",
              },
            }}
            title="Top to Bottom"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 16V4M10 16L6 12M10 16L14 12" stroke={treeDirection === 'top' ? "#4F46E5" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Box>

          {/* Left Direction */}
          <Box
            onClick={() => setTreeDirection('left')}
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              border: treeDirection === 'left' ? "2px solid #4F46E5" : "2px solid #E5E7EB",
              background: treeDirection === 'left' ? "#EEF2FF" : "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#4F46E5",
                background: "#F5F3FF",
                transform: "scale(1.05)",
              },
            }}
            title="Left to Right"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10H16M16 10L12 6M16 10L12 14" stroke={treeDirection === 'left' ? "#4F46E5" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Box>

          {/* Right Direction */}
          <Box
            onClick={() => setTreeDirection('right')}
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              border: treeDirection === 'right' ? "2px solid #4F46E5" : "2px solid #E5E7EB",
              background: treeDirection === 'right' ? "#EEF2FF" : "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#4F46E5",
                background: "#F5F3FF",
                transform: "scale(1.05)",
              },
            }}
            title="Right to Left"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16 10H4M4 10L8 6M4 10L8 14" stroke={treeDirection === 'right' ? "#4F46E5" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Box>

          {/* Bottom Direction */}
          <Box
            onClick={() => setTreeDirection('bottom')}
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
              border: treeDirection === 'bottom' ? "2px solid #4F46E5" : "2px solid #E5E7EB",
              background: treeDirection === 'bottom' ? "#EEF2FF" : "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#4F46E5",
                background: "#F5F3FF",
                transform: "scale(1.05)",
              },
            }}
            title="Bottom to Top"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4V16M10 4L6 8M10 4L14 8" stroke={treeDirection === 'bottom' ? "#4F46E5" : "#6B7280"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Box>
        </Box>

        {/* Model Selector */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 600,
              color: "#374151",
              mr: 1,
            }}
          >
            AI Model:
          </Typography>          

          {availableModels.map((model) => {
            const modelValue = model || model.id;
            const isSelected = selectedModel === modelValue;

            return (
              <Box
                key={modelValue}
                onClick={() => setSelectedModel(modelValue)}
                sx={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  border: isSelected ? "2px solid #4F46E5" : "2px solid #E5E7EB",
                  background: isSelected ? "#EEF2FF" : "#FFFFFF",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#4F46E5",
                    background: "#F5F3FF",
                    transform: "scale(1.05)",
                  },
                }}
                title={model}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? "#4F46E5" : "#6B7280",
                  }}
                >
                  {(model).toUpperCase()}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {(loading && !level1And2Data) || isSwitchingModel ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              p: 3,
            }}
          >
            {/* Skill Tree Skeleton */}
            <Box
              sx={{
                width: "100%",
                maxWidth: "800px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              {/* Root Node Skeleton */}
              <Skeleton
                variant="rectangular"
                width={220}
                height={70}
                sx={{
                  borderRadius: "16px",
                  bgcolor: "rgba(79, 70, 229, 0.1)",
                }}
              />

              {/* Level 1 Nodes Skeleton */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {[1, 2, 3].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    width={200}
                    height={70}
                    sx={{
                      borderRadius: "16px",
                      bgcolor: "rgba(139, 122, 232, 0.1)",
                    }}
                  />
                ))}
              </Box>

              {/* Level 2 Nodes Skeleton */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  maxWidth: "100%",
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Skeleton
                    key={item}
                    variant="rectangular"
                    width={180}
                    height={60}
                    sx={{
                      borderRadius: "16px",
                      bgcolor: "rgba(125, 211, 252, 0.1)",
                    }}
                  />
                ))}
              </Box>

              {/* Loading Text */}
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#6B7280",
                  mt: 2,
                }}
              >
                {isSwitchingModel ? "Switching model..." : "Loading skill tree..."}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {treeData && (
              <Box
                sx={{
                  mb: 2,
                  width: "100%",
                  height: "100%",
                  overflow: "auto",
                  position: "relative",
                }}
              >
                <div
                  id="skill-tree-container"
                  ref={containerRef}
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            )}

            {/* {(level1And2Data?.fromCache || isDataFromCache) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Data loaded from cache. It may not be the most recent.
              </Alert>
            )} */}
          </>
        )}
      </Box>
    </>
  );
};

export default SkillTree;
